import { DocType } from "../../types";
import { syncList } from "./state";
import {
    filterByTypeMemberOf,
    getChunkTypeString,
    mergeRanges,
    rangesAdjacentOrOverlap,
    resolveRange,
} from "./utils";
import type { SyncBaseOptions } from "./types";

/**
 * Merge adjacent chunks vertically (by updatedTimeUtc) for the same type, memberOf, and languages.
 * This combines consecutive time-based chunks that share the same filtering criteria.
 */
export function mergeVertical(options: SyncBaseOptions) {
    // Filter chunks by type, memberOf, and languages (if provided)
    const filteredList = syncList.value.filter(filterByTypeMemberOf(options));

    // Sort in reverse order (newest first)
    filteredList.sort((a, b) => b.blockStart - a.blockStart);

    // Start from the newest chunk and move backwards (vertical merge for the same type, memberOf and languages)
    for (let i = 0; i < filteredList.length - 1; i++) {
        const current = filteredList[i];
        const next = filteredList[i + 1];

        if (
            current.blockEnd <= next.blockStart ||
            // handle responses which did not return any data
            next.blockStart === 0 ||
            // Same-key (same chunkType + memberOf-multiset + languages + publishDate
            // bounds — already enforced by filterByTypeMemberOf above) entries that
            // BOTH carry eof:true necessarily describe the same column. Any gap
            // between them is an artifact of premature sealing (e.g. markColumnEof
            // firing on a wide-inversion calcChunk output that was never actually
            // queried). Consolidate so downstream sees one column instead of two.
            (current.eof === true && next.eof === true)
        ) {
            // Merge chunks — use the older chunk's blockEnd to extend the range.
            // Only preserve current blockEnd for legacy {0, 0} entries (truly empty chunks
            // from before the fix that used queried boundaries for empty responses).
            current.blockEnd =
                next.blockStart === 0 && next.blockEnd === 0
                    ? current.blockEnd
                    : Math.min(current.blockEnd, next.blockEnd);
            current.eof = next.eof;

            // Remove next chunk from syncList
            const index = syncList.value.indexOf(next);
            if (index !== -1) {
                syncList.value.splice(index, 1);

                // Remove merged chunk from local list
                filteredList.splice(i + 1, 1);
            }

            i--; // Re-evaluate current index after merge
        }
    }

    // Calculate the final blockStart and blockEnd from the merged chunks
    const blockStart = filteredList.length ? filteredList[0].blockStart : 0;
    const blockEnd = filteredList.length ? filteredList[0].blockEnd : 0;

    // eof is true only when ALL remaining chunks have reached eof.
    // This prevents prematurely stopping sync when a catch-up fetch (for new data)
    // reaches eof but older incomplete chunks still exist from an interrupted sync.
    const eof = filteredList.length > 0 ? filteredList.every((chunk) => chunk.eof === true) : false;

    return { eof, blockStart, blockEnd };
}

/**
 * Merge chunks horizontally (by memberOf groups and languages) for the same type.
 * Only chunks that have reached EOF can be merged horizontally.
 * This combines different memberOf groups and languages that have complete data for overlapping time ranges.
 */
export function mergeHorizontal(options: { type: DocType; subType?: DocType }) {
    const list = syncList.value.filter(
        (chunk) =>
            chunk.chunkType === getChunkTypeString(options.type, options.subType) && chunk.eof,
    );

    // If no chunks, return default values
    if (list.length === 0) return { blockStart: 0, blockEnd: 0 };

    // If only one chunk, no merge needed
    if (list.length === 1) return { blockStart: list[0].blockStart, blockEnd: list[0].blockEnd };

    // Do horizontal merge for adjacent chunks.
    // Only columns that have reached eof can be merged. Two EOF chunks may merge
    // horizontally only if their publishDate ranges are adjacent-or-overlapping —
    // unioning memberOf/languages across non-mergeable date windows would silently
    // mix data from incompatible columns.
    for (let i = 0; i < list.length; i++) {
        const base = list[i];

        for (let j = 0; j < list.length; j++) {
            if (i === j) continue;
            const compare = list[j];

            const baseRange = resolveRange(base.publishDateMin, base.publishDateMax);
            const compareRange = resolveRange(compare.publishDateMin, compare.publishDateMax);
            if (!rangesAdjacentOrOverlap(baseRange, compareRange)) {
                continue;
            }

            // Merge memberOf groups
            const mergedGroups = Array.from(
                new Set([...base.memberOf, ...compare.memberOf]),
            ).sort();
            base.memberOf = mergedGroups;

            // Merge languages
            if (base.languages || compare.languages) {
                const mergedLanguages = Array.from(
                    new Set([...(base.languages || []), ...(compare.languages || [])]),
                ).sort();
                base.languages = mergedLanguages;
            }

            // Merge publishDate range (concrete numbers after resolveRange)
            const mergedRange = mergeRanges(baseRange, compareRange);
            base.publishDateMin = mergedRange.min;
            base.publishDateMax = mergedRange.max;

            // Update blockStart and blockEnd
            base.blockStart = Math.max(base.blockStart, compare.blockStart);
            base.blockEnd = Math.min(base.blockEnd, compare.blockEnd);

            // Remove compare chunk
            const index = syncList.value.indexOf(compare);
            if (index !== -1) {
                syncList.value.splice(index, 1);

                // Remove merged chunk from local list
                list.splice(j, 1);
            }

            j--; // Re-evaluate j index after merge
        }
    }

    // Calculate the final blockStart and blockEnd from the merged chunks
    const blockStart = list[0].blockStart;
    const blockEnd = list[0].blockEnd;

    return { blockStart, blockEnd };
}

/**
 * Merge vertical and horizontal chunks
 */
export function merge(options: SyncBaseOptions) {
    // Merge vertical chunks
    const mergeResult = mergeVertical(options);

    // If end of file, perform horizontal merge with any complete columns
    if (mergeResult.eof) {
        const r = mergeHorizontal(options);
        mergeResult.blockStart = r.blockStart;
        mergeResult.blockEnd = r.blockEnd;
    }

    return mergeResult;
}
